import { callOpenAI, OPENAI_MODELS } from "../api/openai";
import {
  getDailyQuestRowsInRange,
  getProfile,
  upsertProfile,
} from "../api/supabaseData";
import { getTitleForLevel } from "./levelUtils";
import {
  buildHunterProfileContext,
  buildMemoryContext,
  buildSystemPrompt,
  getLevelCatalogText,
} from "./systemPrompts";
import { buildSystemMemory } from "./systemMemory";

// --- helpers ---------------------------------------------------------

const pad2 = (n) => String(n).padStart(2, "0");
const dstr = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

function startOfLocalDay(date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function endOfLocalDay(date) {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
}

function flattenQuestRows(rows = []) {
  return rows.flatMap((row) =>
    (row?.quests || []).map((quest) => ({
      ...quest,
      __date: row.quest_date,
    }))
  );
}

function filterQuestsByLevelWindow(quests, fromTime = null, toTime = null) {
  if (!fromTime && !toTime) return quests;

  return quests.filter((quest) => {
    const completedAt = quest?.completedAt ? new Date(quest.completedAt) : null;

    if (completedAt && !Number.isNaN(completedAt.getTime())) {
      if (fromTime && completedAt < fromTime) return false;
      if (toTime && completedAt > toTime) return false;
      return true;
    }

    const questDate = quest?.__date ? startOfLocalDay(new Date(`${quest.__date}T00:00:00`)) : null;
    if (!questDate || Number.isNaN(questDate.getTime())) return true;
    if (fromTime && questDate < startOfLocalDay(fromTime)) return false;
    if (toTime && questDate > endOfLocalDay(toTime)) return false;
    return true;
  });
}

async function fetchQuestsSinceLastLevelUp(uid) {
  const profile = await getProfile(uid);
  const today = new Date();
  const fallbackStart = profile?.createdAt ? new Date(profile.createdAt) : new Date(today);
  const fromTime = profile?.lastLevelUpShownAt
    ? new Date(profile.lastLevelUpShownAt)
    : fallbackStart;
  const toTime = new Date();

  const fromDate = dstr(startOfLocalDay(fromTime));
  const toDate = dstr(startOfLocalDay(today));
  const rows = await getDailyQuestRowsInRange(uid, fromDate, toDate);
  const quests = filterQuestsByLevelWindow(flattenQuestRows(rows), fromTime, toTime);

  return {
    quests,
    fromTime,
    toTime,
  };
}

function summarizePerformance(quests) {
  let total = 0, completed = 0, xpSum = 0, xpCount = 0, penalties = 0;
  const bag = new Map();

  for (const q of quests) {
    total++;
    if (q.completed) completed++;
    const xp = Number(q.xp) || 0;
    if (xp) { xpSum += xp; xpCount++; }
    if (q.isPenalty) penalties++;

    const text = [q.title, q.desc].filter(Boolean).join(" ").toLowerCase();
    const words = text.split(/[^a-z0-9]+/).filter(w => w.length >= 3);
    for (const w of words) bag.set(w, (bag.get(w) || 0) + 1);
  }

  const themes = Array.from(bag.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([term, count]) => ({ term, count }));

  return {
    sampleDays: new Set(quests.map(q => q.__date)).size,
    totalQuests: total,
    completedQuests: completed,
    completionRate: total ? Math.round((completed / total) * 100) : 0,
    avgXPPerQuest: xpCount ? Math.round(xpSum / xpCount) : 0,
    penaltiesCount: penalties,
    topThemes: themes
  };
}

function levelCatalog() {
  return getLevelCatalogText();
}

function clampXPForLevel(level, xp) {
  const numericLevel = Math.max(1, Number(level) || 1);
  const parsedXP = Number(xp);
  const minXP = 20 + numericLevel * 5;
  const maxXP = 80 + numericLevel * 25;
  return Number.isFinite(parsedXP)
    ? Math.max(minXP, Math.min(maxXP, parsedXP))
    : minXP;
}


// --- public API ------------------------------------------------------

/**
 * Build the level-up payload by analyzing recent performance and calling OpenAI.
 * Returns: { motivation, criticism: string[], quest: { title, desc, xp } }
 */
export async function generateLevelUpPayload(uid, currentLevel) {
  const { quests: recent, fromTime, toTime } = await fetchQuestsSinceLastLevelUp(uid);
  const memory = await buildSystemMemory(uid);
  const profile = await getProfile(uid);
  const summary = summarizePerformance(recent);

  const lvl = Number(currentLevel) || 1;
  const currentTitle = getTitleForLevel(lvl);
  const windowLabel = `${dstr(startOfLocalDay(fromTime))} to ${dstr(startOfLocalDay(toTime))}`;

  const prompt = buildSystemPrompt({
    contextLines: [
      "Authoritative level catalog:",
      levelCatalog(),
      `The hunter has just reached Level ${lvl} - ${currentTitle}.`,
      `Analyze only the current level window: ${windowLabel}.`,
      buildHunterProfileContext(profile),
      `Previous-level performance JSON: ${JSON.stringify(summary)}`,
      buildMemoryContext(memory),
    ],
    instructionLines: [
      "Analyze only the performance recorded since the last level-up up to now.",
      "Use the hunter record to make the assessment feel historically aware.",
      "Reference long-term strengths, failures, or abandoned patterns naturally when relevant.",
      "Do not mechanically list every memory value.",
      "Write motivation in 2 to 3 short sentences that are celebratory but grounded.",
      "Write criticism as 2 to 4 short bullet-like lines that are constructive and actionable.",
      "Generate one realistic and measurable follow-up quest scaled appropriately for this level.",
    ],
    outputLines: [
      'Return JSON only in this shape: {"motivation":"string","criticism":["point 1","point 2"],"quest":{"title":"...","desc":"...","xp":number}}',
      "No markdown, no code fences, no extra commentary.",
    ],
  });

  let data = {
    motivation: `Level ${lvl} attained. Power condenses.`,
    criticism: ["Maintain consistency.", "Sharpen focus on core habits."],
    quest: {
      title: "Maintain the ascent",
      desc: "Complete your highest-value habit again tomorrow.",
      xp: clampXPForLevel(lvl, null),
    },
  };

  try {
    const res = await callOpenAI(prompt, 500, 0.7, { model: OPENAI_MODELS.ANALYSIS });
    const raw = (res?.result || "").trim();
    const cleaned = raw.replace(/^```json\s*|\s*```$/g, "");
    const parsed = JSON.parse(cleaned);

    data.motivation = String(parsed.motivation || data.motivation);
    data.criticism = Array.isArray(parsed.criticism) ? parsed.criticism.slice(0, 5) : data.criticism;
    const q = parsed.quest || {};
    data.quest = {
      title: q.title || data.quest.title,
      desc: q.desc || data.quest.desc,
      xp: clampXPForLevel(lvl, q.xp)
    };
  } catch (e) {
    console.error("Level-up generation failed:", e);
  }

  return { ...data, level: lvl, title: currentTitle };
}

/** Mark that we showed the level-up popup for this level (avoid repeats). */
export async function markLevelUpShown(uid, level) {
  await upsertProfile(uid, {
    lastLevelUpPopupLevel: level,
    lastLevelUpShownAt: new Date().toISOString(),
  });
}
